import { useId, useState } from "react";

import { Button } from "../../components/Button";
import { Notice } from "../../components/Feedback";
import { Icon } from "../../components/Icon";
import { formatCount } from "../../lib/format";
import type { FieldSpec, ImportKind, ImportPreview } from "../../types/api";
import { missingRequired, type SideState } from "./wizardState";

const SHAREABLE = new Set<FieldSpec["type"]>(["identifier", "text"]);

interface MappingCardProps {
  kind: ImportKind;
  side: SideState;
  busy: boolean;
  onMap: (field: string, column: string | null) => void;
  onLayout: (layout: { sheet?: string; headerRow?: number }) => void;
  onSaveMapping: (enabled: boolean, name?: string) => void;
}

function sampleFor(preview: ImportPreview, column: string | null | undefined): string | null {
  if (!column) return null;
  const index = preview.headers.indexOf(column);
  if (index < 0) return null;
  const value = preview.sample_rows.map((row) => row[index]).find((cell) => cell);
  return value ?? null;
}

function conflicts(preview: ImportPreview, mapping: SideState["mapping"], field: FieldSpec): string | null {
  const column = mapping[field.key];
  if (!column) return null;
  const other = preview.fields.find(
    (candidate) =>
      candidate.key !== field.key &&
      mapping[candidate.key] === column &&
      !(SHAREABLE.has(candidate.type) && SHAREABLE.has(field.type)),
  );
  return other ? `Also mapped to ${other.label}. Each number column can only be used once.` : null;
}

function FieldRow({ preview, side, field, onMap }: { preview: ImportPreview; side: SideState; field: FieldSpec; onMap: MappingCardProps["onMap"] }) {
  const id = useId();
  const column = side.mapping[field.key] ?? "";
  const sample = sampleFor(preview, column);
  const conflict = conflicts(preview, side.mapping, field);
  const suggestion = preview.suggestions[field.key];
  return (
    <div className={`field-row${field.required && !column ? " field-row--missing" : ""}`}>
      <label htmlFor={id} className="field-row__label">
        {field.label}
        {field.required ? <span className="required-mark"> required</span> : null}
      </label>
      <div className="field-row__control">
        <select id={id} value={column} onChange={(event) => onMap(field.key, event.target.value || null)}>
          <option value="">— Not mapped —</option>
          {preview.headers.map((header) => (
            <option key={header} value={header}>
              {header}
            </option>
          ))}
        </select>
        <span className="field-row__meta">
          {sample ? <>e.g. {sample}</> : field.required && !column ? field.description : null}
          {suggestion?.confidence === "fuzzy" && suggestion.column === column ? " · best guess, please check" : null}
        </span>
        {conflict ? <span className="field-error">{conflict}</span> : null}
      </div>
    </div>
  );
}

function MappingCard({ kind, side, busy, onMap, onLayout, onSaveMapping }: MappingCardProps) {
  const preview = side.preview;
  const [headerRow, setHeaderRow] = useState(String(preview?.header_row ?? 1));
  const nameId = useId();
  if (!preview) return null;
  const missing = missingRequired(side);
  const labels = Object.fromEntries(preview.fields.map((field) => [field.key, field.label]));
  const required = preview.fields.filter((field) => field.required);
  const optional = preview.fields.filter((field) => !field.required);
  const optionalMapped = optional.filter((field) => side.mapping[field.key]).length;
  const title = kind === "shipment" ? "Shipment file" : "Invoice file";

  function applyHeaderRow() {
    const value = Number.parseInt(headerRow, 10);
    if (Number.isFinite(value) && value >= 1 && value !== preview?.header_row) onLayout({ headerRow: value });
  }

  return (
    <section className="panel mapping-card" aria-labelledby={`${kind}-mapping-title`} aria-busy={busy}>
      <header className="mapping-card__header">
        <div>
          <p className="eyebrow">{title}</p>
          <h2 id={`${kind}-mapping-title`}>{preview.filename}</h2>
          <p className="muted">
            {formatCount(preview.row_count)} data rows · headers on row {preview.header_row}
          </p>
        </div>
        <div className="mapping-card__layout">
          {preview.sheet_names.length > 1 ? (
            <label>
              <span className="field-label">Worksheet</span>
              <select value={preview.sheet_name ?? ""} disabled={busy} onChange={(event) => onLayout({ sheet: event.target.value })}>
                {preview.sheet_names.map((name) => (
                  <option key={name}>{name}</option>
                ))}
              </select>
            </label>
          ) : null}
          <label>
            <span className="field-label">Header row</span>
            <input
              type="number"
              min={1}
              inputMode="numeric"
              value={headerRow}
              disabled={busy}
              onChange={(event) => setHeaderRow(event.target.value)}
              onBlur={applyHeaderRow}
              onKeyDown={(event) => {
                if (event.key === "Enter") applyHeaderRow();
              }}
            />
          </label>
        </div>
      </header>

      {preview.saved_mapping ? (
        <Notice tone="success" title={`Detected format: ${preview.saved_mapping.name}`}>
          <p>✓ {preview.saved_mapping.mapped_fields} fields mapped automatically from your saved mapping.</p>
        </Notice>
      ) : null}

      <div className="field-list">
        {required.map((field) => (
          <FieldRow key={field.key} preview={preview} side={side} field={field} onMap={onMap} />
        ))}
      </div>

      <details className="more-fields">
        <summary>
          <Icon name="chevron-down" /> More fields ({optionalMapped} of {optional.length} mapped)
        </summary>
        <div className="field-list">
          {optional.map((field) => (
            <FieldRow key={field.key} preview={preview} side={side} field={field} onMap={onMap} />
          ))}
        </div>
      </details>

      <div className="mapping-card__status" role="status">
        {missing.length === 0 ? (
          <p className="status-line status-line--ok">
            <Icon name="check" /> Required fields mapped
          </p>
        ) : (
          missing.map((key) => (
            <p key={key} className="status-line status-line--missing">
              <Icon name="alert" /> {labels[key]} has not been mapped
            </p>
          ))
        )}
      </div>

      <div className="save-mapping">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={side.saveMapping}
            onChange={(event) => onSaveMapping(event.target.checked)}
          />
          Save this mapping for next time
        </label>
        {side.saveMapping ? (
          <label htmlFor={nameId} className="save-mapping__name">
            <span className="visually-hidden">Mapping name</span>
            <input
              id={nameId}
              value={side.mappingName}
              maxLength={120}
              placeholder="e.g. Ridgeline invoice export"
              onChange={(event) => onSaveMapping(true, event.target.value)}
            />
          </label>
        ) : null}
      </div>
    </section>
  );
}

interface MappingStepProps {
  shipment: SideState;
  invoice: SideState;
  busy: Record<ImportKind, boolean>;
  onMap: (kind: ImportKind, field: string, column: string | null) => void;
  onLayout: (kind: ImportKind, layout: { sheet?: string; headerRow?: number }) => void;
  onSaveMapping: (kind: ImportKind, enabled: boolean, name?: string) => void;
  onBack: () => void;
  onContinue: () => void;
}

export function MappingStep({ shipment, invoice, busy, onMap, onLayout, onSaveMapping, onBack, onContinue }: MappingStepProps) {
  const complete = missingRequired(shipment).length === 0 && missingRequired(invoice).length === 0;
  const namesOk = [shipment, invoice].every((side) => !side.saveMapping || side.mappingName.trim());
  return (
    <section aria-labelledby="mapping-heading">
      <div className="step-heading">
        <h1 id="mapping-heading">Confirm your columns</h1>
        <p className="muted">
          We matched spreadsheet columns to FreightAudit fields. Check the required ones, then continue.
        </p>
      </div>
      <div className="mapping-grid">
        {(["shipment", "invoice"] as const).map((kind) => {
          const side = kind === "shipment" ? shipment : invoice;
          return (
          <MappingCard
            key={`${kind}-${side.preview?.import_id}-${side.preview?.sheet_name}-${side.preview?.header_row}`}
            kind={kind}
            side={side}
            busy={busy[kind]}
            onMap={(field, column) => onMap(kind, field, column)}
            onLayout={(layout) => onLayout(kind, layout)}
            onSaveMapping={(enabled, name) => onSaveMapping(kind, enabled, name)}
          />
          );
        })}
      </div>
      <div className="step-actions">
        <Button variant="ghost" icon="arrow-left" onClick={onBack}>
          Back
        </Button>
        <Button variant="primary" iconAfter="arrow-right" onClick={onContinue} disabled={!complete || !namesOk || busy.shipment || busy.invoice}>
          Continue
        </Button>
      </div>
    </section>
  );
}
